import { apiService } from '@/shared/services/api/api.service';

export interface TestQuestion {
  id: string;
  questionText: string;
  type: 'MULTIPLE_CHOICE' | 'TRUE_FALSE';
  options: string[];
  displayOrder: number;
}

export interface Test {
  id: string;
  name: string;
  description: string | null;
  passingScore: number;
  timeLimit: number | null;
  totalQuestions: number;
  questions: TestQuestion[];
}

export interface SubmitTestRequest {
  testId: string;
  answers: {
    questionId: string;
    selectedAnswer: string;
  }[];
  timeSpent?: number;
}

export interface TestResult {
  attemptId: string;
  score: number;
  passed: boolean;
  totalQuestions: number;
  correctAnswers: number;
  passingScore: number;
  answers: {
    questionId: string;
    questionText: string;
    selectedAnswer: string;
    correctAnswer: string;
    isCorrect: boolean;
    explanation: string | null;
  }[];
}

export interface TestAttemptSummary {
  id: string;
  score: number;
  passed: boolean;
  totalQuestions: number;
  correctAnswers: number;
  attemptedAt: string;
  completedAt: string | null;
}

export interface UserTestStatus {
  test: {
    id: string;
    name: string;
    description: string | null;
    passingScore: number;
    totalQuestions: number;
  };
  attempts: TestAttemptSummary[];
  bestScore: number | null;
  hasPassed: boolean;
  canRetake: boolean;
}

class ComplianceTestService {
  /**
   * Get test for current user
   */
  async getMyTest(): Promise<Test> {
    const response = await apiService.get<{ success: boolean; data: Test }>(
      '/compliance-tests/my-test'
    );
    return response.data.data;
  }

  /**
   * Submit test answers
   */
  async submitTest(data: SubmitTestRequest): Promise<TestResult> {
    const response = await apiService.post<{ success: boolean; data: TestResult }>(
      '/compliance-tests/submit',
      data
    );
    return response.data.data;
  }

  /**
   * Get user's test status and attempt history
   */
  async getMyStatus(): Promise<UserTestStatus> {
    const response = await apiService.get<{ success: boolean; data: UserTestStatus }>(
      '/compliance-tests/my-status'
    );
    return response.data.data;
  }

  /**
   * Get specific attempt results
   */
  async getAttemptResults(attemptId: string): Promise<TestResult> {
    const response = await apiService.get<{ success: boolean; data: TestResult }>(
      `/compliance-tests/attempts/${attemptId}`
    );
    return response.data.data;
  }
}

export const complianceTestService = new ComplianceTestService();
